import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { ComboboxState } from './useCombobox';

export type ComboboxPopupProps<T> = {
  combobox: Pick<ComboboxState<T>, 'listboxId' | 'optionId' | 'activeIndex' | 'setActive' | 'pick'>;
  options: readonly T[];
  optionKey: (option: T) => string;
  renderOption: (option: T) => ReactNode;
  /** Extra row classes, e.g. to set a "create" row apart. */
  optionClassName?: (option: T) => string | undefined;
  emptyLabel: string;
  /** Content at the top of the panel, before the caption (a search field). */
  top?: ReactNode;
  /** Small caption above the rows ("Références existantes · 11"). */
  header?: ReactNode;
  /** Line under the rows ("et 12 autres"). */
  footer?: ReactNode;
  /** Accessible name of the list. */
  listLabel?: string;
};

/** The dropdown panel of a combobox: caption, listbox rows (highlight follows the mouse), footer. */
export function ComboboxPopup<T>({
  combobox,
  options,
  optionKey,
  renderOption,
  optionClassName,
  emptyLabel,
  top,
  header,
  footer,
  listLabel,
}: ComboboxPopupProps<T>) {
  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
      {top}
      {header && <div className="eyebrow px-3 pb-1 pt-2">{header}</div>}
      <ul
        id={combobox.listboxId}
        role="listbox"
        aria-label={listLabel}
        className="max-h-60 overflow-y-auto py-1"
      >
        {options.length === 0 ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">{emptyLabel}</li>
        ) : (
          options.map((option, index) => (
            <li
              key={optionKey(option)}
              id={combobox.optionId(index)}
              role="option"
              aria-selected={index === combobox.activeIndex}
              onMouseDown={(event) => {
                event.preventDefault();
                combobox.pick(option);
              }}
              onMouseEnter={() => combobox.setActive(index)}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm',
                index === combobox.activeIndex && 'bg-primary/10',
                optionClassName?.(option)
              )}
            >
              {renderOption(option)}
            </li>
          ))
        )}
      </ul>
      {footer && (
        <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Wraps the part of `text` that matches `query` (case-insensitive) in a highlighted `<mark>`. */
export function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim().toLowerCase();
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-transparent font-bold text-primary">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}
